import { useCallback, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";

interface Point { x: number; y: number }
export interface ZoomSelection { left: number; top: number; width: number; height: number }
interface ZoomView { scale: number; centerX: number; centerY: number }

export function useAreaZoom(options: { maxScale?: number; onTap?: () => void } = {}) {
  const { maxScale = 8 } = options;
  const onTapRef = useRef(options.onTap);
  const start = useRef<Point | null>(null);
  const [selection, setSelection] = useState<ZoomSelection | null>(null);
  const [view, setView] = useState<ZoomView>({ scale: 1, centerX: 0.5, centerY: 0.5 });
  onTapRef.current = options.onTap;

  const point = (event: ReactPointerEvent<HTMLElement>): Point => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: bounds.width ? clamp((event.clientX - bounds.left) / bounds.width) : 0,
      y: bounds.height ? clamp((event.clientY - bounds.top) / bounds.height) : 0
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest("button,input,.camera-overlay")) return;
    const next = point(event);
    start.current = next;
    setSelection({ left: next.x, top: next.y, width: 0, height: 0 });
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!start.current || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    setSelection(rectangle(start.current, point(event)));
  };

  const finish = (event: ReactPointerEvent<HTMLElement>, cancelled = false) => {
    const initial = start.current;
    if (!initial) return;
    const selected = rectangle(initial, point(event));
    start.current = null;
    setSelection(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (cancelled) return;
    if (selected.width < 0.015 && selected.height < 0.015) {
      onTapRef.current?.();
      setView((current) => current.scale > 1.05
        ? { scale: 1, centerX: 0.5, centerY: 0.5 }
        : current);
      return;
    }
    const relativeScale = Math.max(1, Math.min(1 / Math.max(selected.width, 0.01), 1 / Math.max(selected.height, 0.01)));
    const selectedCenterX = selected.left + selected.width / 2;
    const selectedCenterY = selected.top + selected.height / 2;
    setView((current) => ({
      scale: Math.min(maxScale, current.scale * relativeScale),
      centerX: clamp(current.centerX + (selectedCenterX - 0.5) / current.scale),
      centerY: clamp(current.centerY + (selectedCenterY - 0.5) / current.scale)
    }));
  };

  const resetZoom = useCallback(() => setView({ scale: 1, centerX: 0.5, centerY: 0.5 }), []);

  const videoStyle: CSSProperties = {
    transform: `translate(${(0.5 - view.scale * view.centerX) * 100}%, ${(0.5 - view.scale * view.centerY) * 100}%) scale(${view.scale})`,
    transformOrigin: "0 0"
  };

  return {
    selection,
    videoStyle,
    resetZoom,
    surfaceProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: (event: ReactPointerEvent<HTMLElement>) => finish(event),
      onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => finish(event, true)
    }
  };
}

function rectangle(a: Point, b: Point): ZoomSelection {
  return { left: Math.min(a.x, b.x), top: Math.min(a.y, b.y), width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}

function clamp(value: number): number { return Math.min(1, Math.max(0, value)); }
